import React, { useState, useEffect, useRef } from "react";
import { useI18n } from "../i18n/i18n";
import { formatPromptDateLabel } from "../utils/formatDateLabels";

export default function WorkoutNoteModal({
    isOpen,
    date,
    initialNote = "",
    onSave,
    onClose,
}) {
    const { t, locale } = useI18n();
    const [noteText, setNoteText] = useState(initialNote || "");
    const textareaRef = useRef(null);

    const hasExistingNote = Boolean(initialNote && initialNote.trim().length > 0);

    useEffect(() => {
        if (isOpen) {
            setNoteText(initialNote || "");
            const timer = setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    textareaRef.current.setSelectionRange(
                        textareaRef.current.value.length,
                        textareaRef.current.value.length
                    );
                }
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [isOpen, initialNote]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    const handleTextareaKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
            e.preventDefault();
            onSave(date, noteText.trim());
        }
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        onSave(date, noteText.trim());
    };

    const handleDelete = () => {
        onSave(date, "");
    };

    const formattedDate = date ? formatPromptDateLabel(date, locale) : "";

    return (
        <div
            className="workoutNoteModalOverlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="workoutNoteModalTitle"
            onClick={onClose}
        >
            <div
                className="workoutNoteModalContent"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="workoutNoteModalHeader">
                    <div className="workoutNoteModalTitleBlock">
                        <h3 id="workoutNoteModalTitle" className="workoutNoteModalTitle">
                            {hasExistingNote ? t("home.noteModalTitleEdit") : t("home.noteModalTitleAdd")}
                        </h3>
                        {formattedDate && (
                            <span className="workoutNoteModalDateSubtitle">
                                {formattedDate}
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        className="workoutNoteModalClose"
                        onClick={onClose}
                        aria-label={t("common.close")}
                    >
                        &times;
                    </button>
                </div>

                <form onSubmit={handleFormSubmit} className="workoutNoteModalBody">
                    <textarea
                        id="workoutNoteTextarea"
                        ref={textareaRef}
                        className="workoutNoteTextarea"
                        rows={5}
                        maxLength={1000}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        onKeyDown={handleTextareaKeyDown}
                        placeholder={t("home.notePlaceholder")}
                        aria-label={t("home.note")}
                    />

                    <div className="workoutNoteCharCount">
                        {t("home.charCount", { current: noteText.length, max: 1000 })}
                    </div>

                    <div className="workoutNoteModalActions">
                        {hasExistingNote && (
                            <button
                                type="button"
                                className="workoutNoteDeleteButton"
                                onClick={handleDelete}
                            >
                                {t("home.deleteNote")}
                            </button>
                        )}
                        <button
                            type="button"
                            className="appButton appButton--secondary workoutNoteCancelButton"
                            onClick={onClose}
                        >
                            {t("common.cancel")}
                        </button>
                        <button
                            type="submit"
                            className="appButton workoutNoteSaveButton"
                        >
                            {t("common.save")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
